import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Box, Collapse, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type DragEvent, type FC, type KeyboardEvent, useRef, useState } from "react";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK, MOTION, SHADOW } from "../../../theme/tokens";
import { MetaChip } from "../Badges/MetaChip";
import SystemButton from "../SystemButton/SystemButton";
import { isFileAccepted } from "./FileDropZone";
import { SystemValidationTextField } from "./SystemValidationTextField";

/**
 * The optional single-file attachment of an `AiPromptComposer`. Passing the group turns the attach
 * affordance on; omitting it makes the composer text-only. Grouped (rather than five loose optional
 * props) so the type itself guarantees a caller that wants attachments supplies every piece it needs.
 */
export type AiPromptComposerAttachment = {
	/** Comma-separated extension list, e.g. `".png,.jpg,.jpeg,.webp"`. Filters both picks and drops. */
	accept: string;
	file: File | null;
	onFileChange: (file: File | null) => void;
	/** Already-translated: the attach button's accessible name / tooltip. */
	attachLabel: string;
	/** Already-translated: the attachment chip's remove button. */
	removeLabel: string;
	/** Already-translated: shown when a picked or dropped file's extension isn't in `accept`. */
	invalidTypeLabel: string;
};

type Props = {
	value: string;
	onChange: (value: string) => void;
	/** Already-translated placeholder — the composer's only resting-state copy when collapsible. */
	placeholder: string;
	/** Already-translated label of the submit button. */
	submitLabel: string;
	onSubmit: () => void;
	/** Already-translated muted one-liner above the action row (what to ask for, how to send). */
	hint?: string;
	isSubmitting?: boolean;
	disabled?: boolean;
	attachment?: AiPromptComposerAttachment;
	/**
	 * Rest as a one-line field and reveal the hint + action row only on focus (or once there is
	 * content). For narrow side panels where an always-expanded composer would crowd the controls
	 * under it. Off by default — the composer is fully expanded.
	 */
	collapsible?: boolean;
	/** Maximum rows the field grows to before it scrolls. */
	maxRows?: number;
	/**
	 * Hard character cap on the prompt, matching whatever the caller's backend enforces. Omitted
	 * means no cap — but a caller whose server has one should pass it, or the user only finds out
	 * after a failed round trip.
	 */
	maxLength?: number;
	autoFocus?: boolean;
	/** Layout overrides only — the caller owns width/margins, not the composer's ink. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A prompt composer for an AI-assisted action: a self-growing multiline field, an optional single-file
 * attachment (compact attach button + a removable chip, drag-and-drop onto the composer included), and a
 * submit button with a loading state that is disabled until there is something to send.
 *
 * Generic over any "describe what you want, optionally show an example" surface — the caller owns every
 * string, the mutation and what to do with the result; this owns only the input shape. All color comes
 * from theme tokens (a brand-tinted surface that lifts on focus), so it re-tints with the org's accent.
 *
 * `collapsible` is the narrow-container mode: it rests as one line and grows on focus, so the composer
 * can live at the top of a 280px settings panel without pushing the settings out of view.
 *
 * Submitting is Cmd/Ctrl + Enter (plain Enter inserts a newline — the prompt is prose, and a stray
 * Enter must not fire an expensive, applied-immediately request).
 */
export const AiPromptComposer: FC<Props> = ({
	value,
	onChange,
	placeholder,
	submitLabel,
	onSubmit,
	hint,
	isSubmitting = false,
	disabled = false,
	attachment,
	collapsible = false,
	// DEFAULT: the field grows with the text and then scrolls. Six rows is where it stops taking
	// space from the controls it sits above in a 280px settings panel — past that the request is a
	// paste, not an instruction. Callers with more room pass their own.
	maxRows = 6,
	maxLength,
	autoFocus = false,
	sx,
	dataTestId,
}) => {
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
	const [hasRejectedFile, setHasRejectedFile] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const file = attachment?.file ?? null;
	const hasContent = value.trim().length > 0 || file !== null;
	// `hasRejectedFile` belongs here: dropping an unsupported file onto a collapsed, empty composer
	// clears `isDraggingOver` and would otherwise collapse the block in the same frame the rejection
	// message mounts — the one moment the user needs to read it. Dropping is now a primary way in, so
	// this is the common path, not an edge case.
	const isExpanded = !collapsible || isFocused || hasContent || isDraggingOver || hasRejectedFile;
	const canSubmit = hasContent && !disabled && !isSubmitting;

	const submit = () => {
		if (!canSubmit) return;
		onSubmit();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
			event.preventDefault();
			submit();
		}
	};

	const acceptFile = (candidate: File | undefined): void => {
		if (!attachment || !candidate) return;
		if (!isFileAccepted(candidate, attachment.accept)) {
			// Surfaced inline rather than swallowed — a silently ignored pick reads as a broken control.
			setHasRejectedFile(true);
			return;
		}
		setHasRejectedFile(false);
		attachment.onFileChange(candidate);
	};

	const attachControl = attachment ? (
		<>
			<SystemButton
				buttonVariant="borderless"
				size="small"
				disabled={disabled || isSubmitting}
				onClick={() => fileInputRef.current?.click()}
				data-testid={dataTestId ? `${dataTestId}-attach` : undefined}
				aria-label={attachment.attachLabel}
				tooltip={attachment.attachLabel}
				noFullWidthMobile
				leadingIcon={<AttachFileRoundedIcon sx={{ fontSize: ICON_SIZE.CONTROL }} />}
				sx={{ minWidth: 0, px: 0.75, alignSelf: "flex-start", flexShrink: 0 }}
			/>
			<input
				ref={fileInputRef}
				hidden
				type="file"
				accept={attachment.accept}
				aria-label={attachment.attachLabel}
				onChange={(event) => {
					acceptFile(event.target.files?.[0]);
					// Reset so re-picking the same file still fires a change.
					if (fileInputRef.current) fileInputRef.current.value = "";
				}}
			/>
		</>
	) : null;

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		if (!attachment || disabled) return;
		event.preventDefault();
		setIsDraggingOver(false);
		acceptFile(event.dataTransfer.files[0]);
	};

	return (
		<Box
			data-testid={dataTestId}
			onDragOver={
				attachment
					? (event: DragEvent<HTMLDivElement>) => {
							event.preventDefault();
							setIsDraggingOver(true);
						}
					: undefined
			}
			onDragLeave={attachment ? () => setIsDraggingOver(false) : undefined}
			onDrop={attachment ? handleDrop : undefined}
			sx={[
				(theme) => ({
					display: "flex",
					flexDirection: "column",
					borderRadius: 2,
					px: 1,
					pt: 0.5,
					pb: isExpanded ? 1 : 0.5,
					border: `1px solid ${alpha(theme.palette.primary.main, isFocused || isDraggingOver ? 0.45 : 0.22)}`,
					// A brand wash rather than a flat fill: the composer is the one bright thing in a panel of
					// grey toggles, and the gradient keeps it from reading as another input box.
					background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, ALPHA.HERO_WASH)} 0%, ${alpha(
						theme.palette.primary.main,
						ALPHA.HOVER_OVERLAY,
					)} 55%, ${theme.palette.background.paper} 100%)`,
					boxShadow: isFocused || isDraggingOver ? SHADOW.PANEL_SOFT : "none",
					transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}),
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<SystemValidationTextField
				fullWidth
				autoFocus={autoFocus}
				dataTestId={dataTestId ? `${dataTestId}-input` : undefined}
				placeholder={placeholder}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				onKeyDown={handleKeyDown}
				disabled={disabled}
				reserveHeightForHelperText={false}
				injectedProps={{
					multiline: true,
					minRows: 1,
					maxRows,
					// Stops an over-long prompt at the keyboard instead of after a round trip. The server
					// still enforces its own cap — this only saves the user the trip.
					slotProps: maxLength === undefined ? undefined : { htmlInput: { maxLength } },
					onFocus: () => setIsFocused(true),
					onBlur: () => setIsFocused(false),
				}}
				inputProps={{
					startAdornment: (
						<AutoAwesomeIcon
							aria-hidden
							sx={{ fontSize: ICON_SIZE.MICRO, color: "primary.main", alignSelf: "flex-start", mt: 0.25, mr: 0.75, flexShrink: 0 }}
						/>
					),
					// Deliberately on the input row rather than in the collapsing action row below:
					// attaching a file is a first-class way to start, equal to typing. Inside the
					// Collapse it was `unmountOnExit`, so at rest there was no visible way to attach
					// anything at all — the composer read as "type something first".
					endAdornment: attachControl,
				}}
				// The field is the composer's writing surface, not a boxed input of its own — the card
				// around it already carries the border.
				inputSx={{
					"& .MuiOutlinedInput-root": { alignItems: "flex-start", p: 0.5, backgroundColor: "transparent" },
					"& .MuiOutlinedInput-notchedOutline": { border: "none" },
					"& .MuiInputBase-input": { fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45 },
				}}
			/>

			<Collapse in={isExpanded} unmountOnExit>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pt: 0.25 }}>
					{!!file && (
						<MetaChip
							tone="primary"
							icon={<ImageOutlinedIcon />}
							label={file.name}
							tooltip={file.name}
							onRemove={() => attachment?.onFileChange(null)}
							removeLabel={attachment?.removeLabel}
							removeDataTestId={dataTestId ? `${dataTestId}-attachment-remove` : undefined}
							dataTestId={dataTestId ? `${dataTestId}-attachment` : undefined}
							sx={{ alignSelf: "flex-start", maxWidth: "100%" }}
						/>
					)}

					{/* `role="alert"` rather than an `aria-describedby` link to the field: SystemValidationTextField
					    sets its own `aria-describedby={helperTextId}` AFTER spreading `injectedProps`, so an injected
					    one is overwritten — and it lands on the TextField root rather than the native input anyway
					    (the component documents that as a known limitation). A live region is announced on its own,
					    which is what matters here. */}
					{hasRejectedFile && (
						<Typography role="alert" sx={{ fontSize: FONT_SIZE.CAPTION, color: "error.main" }}>
							{attachment?.invalidTypeLabel}
						</Typography>
					)}

					{!!hint && <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>{hint}</Typography>}

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
						<SystemButton
							buttonVariant="primary"
							size="small"
							disabled={!canSubmit}
							loading={isSubmitting}
							onClick={submit}
							data-testid={dataTestId ? `${dataTestId}-submit` : undefined}
							noFullWidthMobile
							leadingIcon={<ArrowUpwardRoundedIcon sx={{ fontSize: ICON_SIZE.MICRO }} />}
						>
							{submitLabel}
						</SystemButton>
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};
